import React, { useEffect } from "react";
import { useUser } from "../contexts/useUser";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMCallsRecursive } from "../dataaccess/fetchLLMCalls";
import EvaluateResponseQuality from "./EvaluateResponseQuality";
import {
  Box,
  Button,
  Paper,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import Calls from "./Calls";
import EvaluateBlockedPrompt from "./EvaluateBlockedPrompt";
import DownloadIcon from "@mui/icons-material/Download";
import { fetchCallLargeContent } from "../dataaccess/fetchCallLargeContent";
import {
  buildUsageTapProfilerCallInput,
  downloadUsageTapProfilerCallInput,
  isS3CallContentReference,
  ResolvedCallContent,
} from "../utils/usageTapProfilerExport";

interface ResponseQualityProps {}

const ResponseQuality: React.FC<ResponseQualityProps> = () => {
  const { project } = useUser();
  const [call, setCall] = React.useState(null);
  const [blockedCall, setBlockedCall] = React.useState(null);
  const [value, setValue] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState("");
  const pageSize = 5;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [limit, setLimit] = React.useState("100");
  const [status, setStatus] = React.useState("200");

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "calls",
      { projectId: project?.id, status: status, limit: limit },
    ],
    queryFn: () =>
      fetchLLMCallsRecursive(project?.id ?? "", parseInt(limit), true, status),

    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  useEffect(() => {
    setPage(0);
    if (value === 1) {
      setStatus("403");
    } else {
      setStatus("200");
    }
  }, [value]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error...</div>;
  }

  const callsData = data?.data?.callsByProjectIdAndCreatedAt?.items ?? [];
  const paginatedData = callsData.slice(page * pageSize, (page + 1) * pageSize);

  const handleClick = (call: any) => {
    setCall(call);
  };

  const handleBlockedClick = (call: any) => {
    setBlockedCall(call);
  };

  const selectedCall: any = value === 0 ? call : blockedCall;

  const handleUsageTapDownload = async () => {
    if (!selectedCall) return;

    setIsDownloading(true);
    setDownloadError("");

    try {
      const resolvedContent: ResolvedCallContent = {};
      const contentFields = ["messages", "prompt", "response"] as const;

      await Promise.all(
        contentFields.map(async (fieldType) => {
          if (isS3CallContentReference(selectedCall[fieldType])) {
            resolvedContent[fieldType] = await fetchCallLargeContent(
              selectedCall.id,
              fieldType
            );
          }
        })
      );

      const input = buildUsageTapProfilerCallInput(
        selectedCall,
        resolvedContent,
        {
          id: project?.id,
          name: project?.name,
          source: "llmasaserviceadmin.review-calls",
        },
        project?.systemInstructions
      );
      downloadUsageTapProfilerCallInput(input);
    } catch (error) {
      console.error("Failed to create UsageTap Profiler export", error);
      setDownloadError(
        error instanceof Error
          ? error.message
          : "Unable to create the UsageTap Profiler export."
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box p={2}>
      <Tabs value={value} onChange={handleChange}>
        <Tab label="Evaluate Recent Responses" />
        <Tab label="Evaluate Blocked Prompts" />
      </Tabs>

      {value === 0 && (
        <Paper>
          <Box p={2}>
            <Typography variant="caption">
              Click on a recent response to evaluate and compare model quality.
            </Typography>
            <Calls
              calls={paginatedData}
              showFilter={false}
              title="Calls"
              onClick={handleClick}
              selectedCall={call}
            />

            <Box mt={2}>
              <Button
                onClick={() => setPage((prevPage) => Math.max(prevPage - 1, 0))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                onClick={() =>
                  setPage((prevPage) =>
                    Math.min(
                      prevPage + 1,
                      Math.ceil(callsData.length / pageSize) - 1
                    )
                  )
                }
                disabled={page === Math.ceil(callsData.length / pageSize) - 1}
              >
                Next
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleUsageTapDownload}
                disabled={!call || isDownloading}
                sx={{ ml: 1 }}
              >
                {isDownloading ? "Preparing JSON..." : "Download for UsageTap"}
              </Button>
              {downloadError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {downloadError}
                </Typography>
              )}
            </Box>
          </Box>
          <hr />

          {call && <EvaluateResponseQuality call={call} />}
        </Paper>
      )}

      {value === 1 && (
        <Paper>
          <Box p={2}>
            <Typography variant="caption">
              Click on a recent response to take action on blocked prompts
            </Typography>
            <Calls
              calls={paginatedData}
              showFilter={false}
              title="Blocked Calls"
              onClick={handleBlockedClick}
              selectedCall={blockedCall}
            />

            <Box mt={2}>
              <Button
                onClick={() => setPage((prevPage) => Math.max(prevPage - 1, 0))}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                onClick={() =>
                  setPage((prevPage) =>
                    Math.min(
                      prevPage + 1,
                      Math.ceil(callsData.length / pageSize) - 1
                    )
                  )
                }
                disabled={page === Math.ceil(callsData.length / pageSize) - 1}
              >
                Next
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleUsageTapDownload}
                disabled={!blockedCall || isDownloading}
                sx={{ ml: 1 }}
              >
                {isDownloading ? "Preparing JSON..." : "Download for UsageTap"}
              </Button>
              {downloadError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {downloadError}
                </Typography>
              )}
            </Box>
          </Box>
          <hr />

          {blockedCall && <EvaluateBlockedPrompt call={blockedCall} />}
        </Paper>
      )}
    </Box>
  );
};

export default ResponseQuality;
